// Live match scores, pushed from our API's /ws/live relay of OverPass's livescore feed
// (PowerLigaStats/Realtime/LiveScoreHub.cs documents the protocol).
//
// One socket per tab, shared by every component through a ref-counted connection: the header,
// match lists and the live pages all read the same state. The socket closes a few seconds after
// the last user unmounts, so navigating between pages doesn't reconnect.
import { useEffect, useMemo, useSyncExternalStore } from "react";

export interface LiveTeam {
  /** OverPass team id — null when the API couldn't match the team (links are skipped then). */
  teamId: string | null;
  name: string;
  logo: string | null;
  score: number;
}

export interface LiveMatch {
  /** OverPass match id: the same value as UpcomingMatch.challonge_id and Match.matchid. */
  matchId: string;
  division: string;
  divisionName: string;
  season: number | null;
  team1: LiveTeam;
  team2: LiveTeam;
  map: string | null;
  mapStatus: number;
  /** The map is over. The API keeps finished maps for ~3 h so a result shows before the demo is parsed. */
  finished: boolean;
  /** ISO time we first saw it finished; null while being played. */
  finishedAt: string | null;
  bestOf: number;
  seriesTeam1: number;
  seriesTeam2: number;
  maxRounds: number;
  overtimeRounds: number;
  startTime: string | null;
}

export interface LivePlayer {
  steamId: string;
  name: string;
  kills: number;
  deaths: number;
  assists: number;
  rounds: number;
  damage: number;
  adr: number | null;
  /** Percent. */
  kast: number | null;
  /** Percent of kills. */
  hsPct: number | null;
  /** HLTV Rating 1.0, as computed by OverPass. */
  rating: number | null;
  firstKills: number;
  firstDeaths: number;
  tradeKills: number;
  utilityDamage: number;
  flashAssists: number;
  enemiesFlashed: number;
  /** Rounds with 2, 3, 4 and 5 kills. */
  multiKills: number[];
}

export interface LiveRound {
  round: number;
  /** 1 = team1, 2 = team2. */
  winner: 1 | 2;
  /** Demo-parser names: TerroristsWin, CTsWin, BombDefused, TargetBombed, TargetSaved, RoundDraw. */
  reason: string;
  ctAlive: number;
  tAlive: number;
}

export interface LiveBoard {
  team1Players: LivePlayer[];
  team2Players: LivePlayer[];
  rounds: LiveRound[];
}

export interface LiveDetail {
  /** Null once the map is no longer in the feed (finished, or never live). */
  match: LiveMatch | null;
  board: LiveBoard | null;
}

/** ws(s)://<api>/ws/live. WS endpoints live at the server root, so the legacy /PowerStats REST
 *  prefix (a Cloudflare-edge path rewrite) is stripped — same as the camera relay. */
export function liveWsUrl(): string {
  const apiBase = (process.env.REACT_APP_API_URL || "http://localhost:5270")
    .replace(/\/PowerStats\/?$/i, "")
    .replace(/\/$/, "");
  return apiBase.replace(/^http/i, "ws") + "/ws/live";
}

// ---------------------------------------------------------------------------
// Shared connection
// ---------------------------------------------------------------------------

interface LiveState {
  matches: LiveMatch[];
  /** A summary has arrived on the current connection (distinguishes "none live" from "loading"). */
  loaded: boolean;
  connected: boolean;
  details: Readonly<Record<string, LiveDetail>>;
}

const IDLE_CLOSE_MS = 8000;

class LiveConnection {
  private state: LiveState = { matches: [], loaded: false, connected: false, details: {} };
  private listeners = new Set<() => void>();
  private socket: WebSocket | null = null;
  private users = 0;
  private watchCounts = new Map<string, number>();
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;

  getState = () => this.state;

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  acquire() {
    this.users++;
    if (this.idleTimer) { clearTimeout(this.idleTimer); this.idleTimer = null; }
    if (!this.socket && !this.reconnectTimer) this.connect();
  }

  release() {
    this.users = Math.max(0, this.users - 1);
    if (this.users > 0 || this.idleTimer) return;
    this.idleTimer = setTimeout(() => {
      this.idleTimer = null;
      if (this.users > 0) return;
      this.shutdown();
    }, IDLE_CLOSE_MS);
  }

  watch(matchId: string) {
    this.watchCounts.set(matchId, (this.watchCounts.get(matchId) ?? 0) + 1);
    this.sendWatch();
  }

  unwatch(matchId: string) {
    const n = (this.watchCounts.get(matchId) ?? 0) - 1;
    if (n > 0) {
      this.watchCounts.set(matchId, n);
      return;
    }
    this.watchCounts.delete(matchId);
    const { [matchId]: _dropped, ...rest } = this.state.details;
    this.set({ details: rest });
    this.sendWatch();
  }

  private set(patch: Partial<LiveState>) {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((l) => l());
  }

  private connect() {
    let ws: WebSocket;
    try {
      ws = new WebSocket(liveWsUrl());
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.socket = ws;

    ws.onopen = () => {
      this.attempt = 0;
      this.set({ connected: true });
      this.sendWatch();
    };
    ws.onmessage = (event) => {
      let msg: any;
      try {
        msg = JSON.parse(event.data);
      } catch {
        return;
      }
      if (msg?.t === "summary" && Array.isArray(msg.matches)) {
        // ready=false: a freshly deployed API that hasn't heard from OverPass yet. Keep the
        // last-known scores instead of flashing "nothing live".
        if (msg.ready === false) return;
        this.set({ matches: msg.matches, loaded: true });
      } else if (msg?.t === "match" && typeof msg.matchId === "string" && this.watchCounts.has(msg.matchId)) {
        this.set({ details: { ...this.state.details, [msg.matchId]: { match: msg.match ?? null, board: msg.board ?? null } } });
      }
    };
    ws.onclose = () => {
      if (this.socket !== ws) return; // superseded by shutdown()
      this.socket = null;
      this.set({ connected: false });
      if (this.users > 0) this.scheduleReconnect();
    };
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) return;
    this.attempt++;
    // 1.5 s steps up to 9 s like the camera page; a server that keeps refusing (feature off,
    // long outage) backs off to a minute. Jitter spreads the reconnect wave after a deploy.
    const base = this.attempt > 10 ? 60000 : 1500 * Math.min(this.attempt, 6);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.users > 0 && !this.socket) this.connect();
    }, base + Math.random() * 1000);
  }

  private sendWatch() {
    const ws = this.socket;
    if (!ws || ws.readyState !== WebSocket.OPEN) return; // re-sent from onopen
    ws.send(JSON.stringify({ t: "watch", matchIds: Array.from(this.watchCounts.keys()) }));
  }

  private shutdown() {
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    const ws = this.socket;
    this.socket = null;
    ws?.close();
    this.attempt = 0;
    this.set({ connected: false, loaded: false, matches: [], details: {} });
  }
}

const connection = new LiveConnection();

const DISABLED_STATE: LiveState = { matches: [], loaded: false, connected: false, details: {} };
const subscribeNothing = () => () => {};
const getDisabledState = () => DISABLED_STATE;

/** `enabled=false` never opens the socket and reads as "nothing live". */
function useLiveState(enabled: boolean): LiveState {
  useEffect(() => {
    if (!enabled) return undefined;
    connection.acquire();
    return () => connection.release();
  }, [enabled]);
  return useSyncExternalStore(
    enabled ? connection.subscribe : subscribeNothing,
    enabled ? connection.getState : getDisabledState,
  );
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Every map in the feed, scores only: `live` is being played, `finished` is over (newest first). */
export function useLiveMatches(enabled = true) {
  const { matches, loaded, connected } = useLiveState(enabled);
  const split = useMemo(() => ({
    live: matches.filter((m) => !m.finished),
    finished: matches
      .filter((m) => m.finished)
      .sort((a, b) => (b.finishedAt ?? "").localeCompare(a.finishedAt ?? "")),
  }), [matches]);
  return { matches, ...split, loaded, connected };
}

/** Live maps keyed by match id — for putting a LIVE badge on an existing match row. */
export function useLiveMatchLookup(enabled = true): Readonly<Record<string, LiveMatch>> {
  const { matches } = useLiveState(enabled);
  return useMemo(() => Object.fromEntries(matches.map((m) => [m.matchId, m])), [matches]);
}

/** One map with its live scoreboard. `loaded` turns true once the server has answered for this
 *  id (with the map, or with null when it isn't live). */
export function useLiveMatch(matchId: string | undefined, enabled = true) {
  const state = useLiveState(enabled);
  useEffect(() => {
    if (!matchId || !enabled) return undefined;
    connection.watch(matchId);
    return () => connection.unwatch(matchId);
  }, [matchId, enabled]);
  const detail = matchId ? state.details[matchId] : undefined;
  return {
    match: detail?.match ?? null,
    board: detail?.board ?? null,
    loaded: detail !== undefined,
    connected: state.connected,
  };
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

/** The live team that is `teamId`'s, when a row shows the teams in a different order than the
 *  feed. Falls back to the feed's order when ids are missing. */
export function orientToTeam1(match: LiveMatch, team1Id: string | null | undefined): [LiveTeam, LiveTeam] {
  if (team1Id && match.team2.teamId === team1Id) return [match.team2, match.team1];
  return [match.team1, match.team2];
}

/** The round being played now (1-based), from the rounds already decided; the last round
 *  once the map is over. */
export function currentRound(match: LiveMatch): number {
  const played = match.team1.score + match.team2.score;
  return match.finished ? played : played + 1;
}
